import { Component, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { SafeUrl } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { ImageService } from 'src/app/services/image.service';
import { AppService } from 'src/app/services/app.service';
import { Icons } from 'src/app/models/icons.model';
import { WebKitDocument } from 'src/app/models/webkit.model';
import { CameraModel } from 'src/app/models/camera.model';
import { SiteModel } from 'src/app/models/site.model';

@Component({
  selector: 'app-snapshot-viewer',
  standalone: false,
  templateUrl: './snapshot-viewer.component.html',
  styleUrls: ['./snapshot-viewer.component.scss'],
})
export class SnapshotViewerComponent implements OnInit, OnDestroy {
  icons = new Icons();
  imageSrc: SafeUrl;
  loadingMsg: string;
  snapshotTime: Date;

  private appLoadedHandler: Subscription = null;
  private appImageRefreshHandler: Subscription = null;
  private routeHandler: Subscription = null;

  get isFullscreen(): boolean {
    return (
      !!document.fullscreenElement ||
      (<WebKitDocument>document).webkitIsFullScreen
    );
  }

  constructor(
    private imageService: ImageService,
    public appService: AppService,
    private route: ActivatedRoute,
    private router: Router
  ) {
    this.appImageRefreshHandler = this.appService.appRefreshImageEvent.subscribe(
      () => {
        if (this.appService.selectedCamera && this.snapshotTime) {
          this.loadingMsg = `Refreshing snapshot: ${this.appService.selectedCamera.Name}`;
          this.getSnapshot();
        }
      }
    );
  }

  ngOnInit(): void {
    if (!this.appService.isAppLoaded) {
      this.appLoadedHandler = this.appService.appLoadedEvent.subscribe(() => {
        this.getRoute();
      });
    } else {
      this.getRoute();
    }
  }

  ngOnDestroy(): void {
    if (this.appLoadedHandler) this.appLoadedHandler.unsubscribe();
    if (this.appImageRefreshHandler) this.appImageRefreshHandler.unsubscribe();
    if (this.routeHandler) this.routeHandler.unsubscribe();
  }

  exitFullscreen() {
    if (document.exitFullscreen) document.exitFullscreen();
    else if ((<WebKitDocument>document).webkitCancelFullScreen)
      (<WebKitDocument>document).webkitCancelFullScreen();
    else if ((<WebKitDocument>document).mozCancelFullScreen)
      (<WebKitDocument>document).mozCancelFullScreen();
  }

  /**
   * Subscribe to route parameters and validate camera access. Only allows viewing snapshots for cameras
   * that exist in the user's accessible sites. If camera is not found in accessible sites or parameters
   * are invalid, redirects to main view.
   */
  private getRoute() {
    this.routeHandler = this.route.paramMap.subscribe((params) => {
      const cameraIdRaw = params.get('cameraId') || this.route.parent?.snapshot.paramMap.get('cameraId');
      const timeRaw = params.get('time') || this.route.parent?.snapshot.paramMap.get('time');
      const cameraId = cameraIdRaw ? +cameraIdRaw : NaN;
      const epochSeconds = timeRaw ? +timeRaw : NaN;

      // Validate route parameters
      if (!Number.isFinite(cameraId) || !Number.isFinite(epochSeconds) || epochSeconds <= 0) {
        this.router.navigate(['/view']);
        return;
      }

      const snapshotTime = new Date(epochSeconds * 1000);
      if (Number.isNaN(snapshotTime.getTime())) {
        this.router.navigate(['/view']);
        return;
      }

      // Ensure sites are loaded before validating camera access
      if (!this.appService.sites || this.appService.sites.length === 0) {
        this.router.navigate(['/view']);
        return;
      }

      // Find camera within user's accessible sites - camera must exist in accessible sites to view
      const match = this.findCameraById(cameraId);
      if (!match) {
        // Camera not found in any accessible site - redirect to prevent unauthorized access
        this.router.navigate(['/view']);
        return;
      }

      this.snapshotTime = snapshotTime;
      this.appService.selectedSite = match.site;
      this.appService.selectedCamera = match.camera;
      this.appService.selectedCamera.HasError = false;

      this.loadingMsg = `Loading snapshot: ${this.appService.selectedCamera.Name}`;
      this.getSnapshot();
    });
  }

  /**
   * Finds a camera by ID within the user's accessible sites only. This ensures users can only
   * view snapshots for cameras they have access to. Returns null if camera is not found in
   * any accessible site.
   */
  private findCameraById(cameraId: number): { site: SiteModel; camera: CameraModel } | null {
    if (!this.appService.sites || this.appService.sites.length === 0) return null;

    // Search through user's accessible sites only
    for (const site of this.appService.sites) {
      const camera = (site.Cameras || []).find((c) => c.Id === cameraId);
      if (camera) {
        // Camera found in an accessible site
        return { site, camera };
      }
    }

    // Camera not found in any accessible site
    return null;
  }

  private async getSnapshot() {
    if (this.appService.selectedCamera && this.snapshotTime) {
      if (!this.appService.selectedCamera.HasError) {
        this.appService.isImageLoading = true;
        this.appService.isImageLoaded = false;

        this.imageService
          .getRecordingSnapshot(this.appService.selectedCamera.Id, this.snapshotTime)
          .then((img) => {
            this.appService.selectedCamera.HasError = false;
            this.imageSrc = img;
          })
          .catch((err) => {
            if (err == 'timeout') {
              this.appService.selectedCamera.ErrorMessage =
                'Snapshot request timed out.';
            } else {
              this.appService.selectedCamera.ErrorMessage = undefined;
              console.log(err);
            }
            this.appService.selectedCamera.HasError = true;
            this.imageSrc = null;
          })
          .finally(() => {
            this.appService.isImageLoaded = true;
            this.appService.isImageLoading = false;
          });
      } else {
        this.imageSrc = null;
      }
    }
  }
}
